import { resources } from '../locales/resources';
import { resolveContactSlug } from './contactSlug';

type Locale = keyof typeof resources;

const supportedLocales = Object.keys(resources) as Locale[];

const domainDefaultLocaleMap: Record<string, string> = {
  fr: 'fr-FR',
  com: 'fr-FR',
  de: 'de-DE',
  es: 'es-ES',
  it: 'it-IT',
  be: 'fr-BE',
  lu: 'fr-LU',
};

const localeToTld: Record<string, string> = {
  'fr-FR': 'com',
  'de-DE': 'de',
  'es-ES': 'es',
  'it-IT': 'it',
  'fr-BE': 'be',
  'nl-BE': 'be',
  'fr-LU': 'lu',
  'de-LU': 'lu',
};

function getTldForLocale(locale: string): string {
  return localeToTld[locale] ?? 'com';
}

function replaceHostnameTld(hostname: string, tld: string): string {
  const parts = hostname.split('.');
  parts[parts.length - 1] = tld;
  return parts.join('.');
}

function isSupportedLocale(locale: string): locale is Locale {
  return supportedLocales.includes(locale as Locale);
}

function getDefaultLocaleFromHostname(hostname: string): Locale {
  const tld = hostname.split('.').pop()?.toLowerCase() ?? '';
  const locale = domainDefaultLocaleMap[tld];

  if (locale && isSupportedLocale(locale)) {
    return locale;
  }

  return 'fr-FR';
}

function parseUrl(url: string | URL): { hostname: string; pathname: string } {
  if (url instanceof URL) {
    return { hostname: url.hostname, pathname: url.pathname };
  }

  if (url.startsWith('http://') || url.startsWith('https://')) {
    const parsed = new URL(url);
    return { hostname: parsed.hostname, pathname: parsed.pathname };
  }

  const pathname = url.startsWith('/') ? url : `/${url}`;

  if (typeof window !== 'undefined') {
    return { hostname: window.location.hostname, pathname };
  }

  return { hostname: '', pathname };
}

export function getLocale(url: string | URL): Locale {
  const { hostname, pathname } = parseUrl(url);
  const defaultLocale = getDefaultLocaleFromHostname(hostname);
  const segments = pathname.split('/').filter(Boolean);
  const localeFromPath = segments[0];

  if (localeFromPath && isSupportedLocale(localeFromPath)) {
    return localeFromPath;
  }

  return defaultLocale;
}

export function getSlug(url: string | URL) {
  let pathname: string;

  if (typeof url === 'string' && !url.startsWith('http')) {
    pathname = url.startsWith('/') ? url : `/${url}`;
  } else {
    pathname = parseUrl(url).pathname;
  }

  const segments = pathname.split('/').filter(Boolean);
  return isSupportedLocale(segments[0])
    ? segments.slice(1).join('/')
    : segments.join('/');
}

function isRootLocale(locale: Locale): boolean {
  return locale === 'fr-FR';
}

export function buildLocalizedPath(slug: string, locale: Locale): string {
  if (isRootLocale(locale)) {
    return slug ? `/${slug}` : '/';
  }

  return slug ? `/${locale}/${slug}` : `/${locale}`;
}

function isExternalUrl(url: string): boolean {
  return /^(https?:|mailto:|tel:|phone:)/i.test(url);
}

export function localizePath(url: string, locale: Locale): string {
  if (!url || isExternalUrl(url)) {
    return url;
  }

  const path = url.startsWith('/') ? url : `/${url}`;
  const slug = resolveContactSlug(getSlug(path), locale);
  return buildLocalizedPath(slug, locale);
}

export function buildLanguageSwitchUrl(options: {
  slug: string;
  locale: Locale;
  hostname: string;
  protocol?: string;
}): string {
  const { slug, locale, hostname, protocol = 'https:' } = options;
  const hostnameWithTld = replaceHostnameTld(hostname, getTldForLocale(locale));
  const path = buildLocalizedPath(slug, locale);

  return `${protocol}//${hostnameWithTld}${path}`;
}

export function isCrossDomainLanguageSwitch(
  currentHostname: string,
  targetLocale: Locale,
): boolean {
  const currentTld = currentHostname.split('.').pop()?.toLowerCase() ?? '';
  return getTldForLocale(targetLocale) !== currentTld;
}
